import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined
} from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  Divider,
  Flex,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Pagination,
  Popconfirm,
  Row,
  Select
} from 'antd'
import { FormListFieldData, TablePaginationConfig } from 'antd/lib'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  ClassifierServiceGetCommonClassifierElementListEndpointDefaultResponse,
  useClassifierServiceCreateCommonClassifierElementEndpoint,
  useClassifierServiceDeleteCommonClassifierElementEndpoint,
  useClassifierServiceGetClassifierEndpoint,
  useClassifierServiceGetCommonClassifierElementEndpointKey,
  useClassifierServiceGetCommonClassifierElementListEndpointKey,
  useClassifierServiceGetSystemKeyListEndpointKey,
  useClassifierServiceSetSystemKeyEndpoint,
  useClassifierServiceUpdateCommonClassifierElementEndpoint
} from 'src/api/queries'
import {
  ApiError,
  ClassifierType,
  ResultValueOptionType
} from 'src/api/requests'
import { LANGUAGE_KEYS } from 'src/app-constants'
import {
  EditableFormItem,
  message,
  requiredRules,
  SaveConfirm
} from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler, resetPagination } from 'src/utils'

import { useSystemKeys } from '../../hooks/useSystemKeys'
import { useStyles } from '../styles'

import { SystemKeyField } from './SystemKeyField'

type Props = {
  data?: ClassifierServiceGetCommonClassifierElementListEndpointDefaultResponse
  fields: FormListFieldData[]
  form: FormInstance
  pagination: TablePaginationConfig
  add: () => void
  remove: (index: number) => void
}

export const ElementsTable = ({
  data,
  pagination,
  fields,
  form,
  add,
  remove
}: Props) => {
  const { styles } = useStyles()
  const { t } = useLocales()
  const { id } = useParams()
  const queryClient = useQueryClient()

  const [editingIndex, setEditingIndex] = useState<number>()

  const [isNewItem, setIsNewItem] = useState(false)

  // Element the selected unique system key is moved from (cleared there before it is set here).
  const [systemKeyMoveFrom, setSystemKeyMoveFrom] = useState<string>()

  const { data: classifier } = useClassifierServiceGetClassifierEndpoint({
    id: String(id)
  })

  const isResultOption =
    classifier?.type === ClassifierType.INVESTIGATION_RESULT_OPTION

  const systemKeys = useSystemKeys(id)
  const { hasKeys: hasSystemKeys, findKey } = systemKeys

  const extraColumns = Number(isResultOption) + Number(hasSystemKeys)

  const positivityOptions = [
    {
      value: ResultValueOptionType.POSITIVE,
      label: t('classifiers.positivity_positive')
    },
    {
      value: ResultValueOptionType.NEGATIVE,
      label: t('classifiers.positivity_negative')
    },
    {
      value: ResultValueOptionType.OTHERS,
      label: t('classifiers.positivity_others')
    }
  ]

  const { mutateAsync: create } =
    useClassifierServiceCreateCommonClassifierElementEndpoint()

  const { mutateAsync: edit } =
    useClassifierServiceUpdateCommonClassifierElementEndpoint()

  const { mutateAsync: setSystemKey } =
    useClassifierServiceSetSystemKeyEndpoint()

  const { mutate: deleteMutation } =
    useClassifierServiceDeleteCommonClassifierElementEndpoint({
      onSuccess: () => {
        onMutationSuccess()
        message.success(t('common.success_delete'))
        resetPagination(pagination)
      },
      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const addItem = async () => {
    const values = await form.validateFields()

    if (values) {
      add()
      setEditingIndex(fields.length)
      setIsNewItem(true)
    }
  }

  const saveSystemKey = async (
    elementId: string,
    key?: string | null
  ): Promise<boolean> => {
    const saved = data?.data?.find((item) => item.id === elementId)
    const savedKey = findKey(saved?.originalValue)?.key ?? null
    const newKey = key ?? null

    // Keys the system owns (OriginalValue not in the classifier's keys) are left untouched.
    if (!hasSystemKeys || newKey === savedKey) return true
    if (saved?.originalValue && !findKey(saved.originalValue)) return true

    try {
      if (newKey && systemKeyMoveFrom) {
        await setSystemKey({
          id: String(id),
          elementId: systemKeyMoveFrom,
          requestBody: { key: null }
        })
      }

      await setSystemKey({
        id: String(id),
        elementId,
        requestBody: { key: newKey }
      })

      return true
    } catch (e) {
      // 409 when the unique key was taken by another element meanwhile (message names the holder).
      formErrorHandler((e as ApiError).body?.errors)

      return false
    }
  }

  const onSave = async () => {
    await form.validateFields()
    const values = form.getFieldValue(['elements', editingIndex])

    const requestBody = {
      translation: values.translation,
      order: values.order,
      comments: values.comments,
      resultPositivity: values.resultPositivity
    }

    let elementId: string | undefined = values?.id

    try {
      if (isNewItem) {
        const created = await create({ id: String(id), requestBody })
        elementId = created.id
      } else {
        await edit({ id: String(id), elementId: values?.id, requestBody })
      }
    } catch (e) {
      formErrorHandler((e as ApiError).body?.errors)
      return
    }

    if (elementId) {
      const keySaved = await saveSystemKey(elementId, values.originalValue)

      if (!keySaved) {
        // The element itself was saved: keep the row in edit mode so the key can be corrected,
        // and refresh the lists so the saved values are current.
        if (isNewItem) {
          form.setFieldValue(['elements', editingIndex, 'id'], elementId)
          setIsNewItem(false)
        }

        invalidateQueries()

        return
      }
    }

    message.success(t('common.save_success'))
    onMutationSuccess()
  }

  const onCancel = (index: number) => {
    if (isNewItem) {
      remove(index)
    } else {
      form.setFieldValue('elements', data?.data)
    }

    setIsNewItem(false)
    setEditingIndex(undefined)
    setSystemKeyMoveFrom(undefined)
  }

  const onDelete = (index: number) => {
    const values = form.getFieldValue(['elements', index])

    deleteMutation({ id: String(id), elementId: values?.id })
  }

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: [useClassifierServiceGetCommonClassifierElementListEndpointKey]
    })
    queryClient.invalidateQueries({
      queryKey: [useClassifierServiceGetCommonClassifierElementEndpointKey]
    })
    queryClient.invalidateQueries({
      queryKey: [useClassifierServiceGetSystemKeyListEndpointKey]
    })
  }

  const onMutationSuccess = () => {
    invalidateQueries()
    setIsNewItem(false)
    setEditingIndex(undefined)
    setSystemKeyMoveFrom(undefined)
  }

  const isDisabled = editingIndex !== undefined

  return (
    <Flex vertical align="center">
      {fields.map(({ name }) => {
        return (
          <div key={name} className="full-width">
            {fields.length >= 2 && name > 0 && <Divider />}

            <Row gutter={[16, 16]} align="bottom">
              {LANGUAGE_KEYS.map((langKey) => {
                return (
                  <Col span={8} key={langKey}>
                    <EditableFormItem
                      isEditing={editingIndex === name}
                      name={[name, 'translation', langKey]}
                      label={`${langKey.toUpperCase()}`}
                      rules={requiredRules}
                      className="full-width"
                    >
                      <Input placeholder={t('common.enter_text')} />
                    </EditableFormItem>
                  </Col>
                )
              })}
              <Col span={8}>
                <EditableFormItem
                  isEditing={editingIndex === name}
                  name={[name, 'order']}
                  label={t('common.order')}
                  rules={requiredRules}
                  className="full-width"
                >
                  <InputNumber
                    className="full-width"
                    min={0}
                    placeholder={t('common.enter_text')}
                  />
                </EditableFormItem>
              </Col>

              {isResultOption && (
                <Col span={8}>
                  <Form.Item
                    name={[name, 'resultPositivity']}
                    label={t('classifiers.result_positivity')}
                    className="full-width"
                  >
                    <Select
                      allowClear
                      disabled={editingIndex !== name}
                      options={positivityOptions}
                      placeholder={t('classifiers.result_positivity')}
                    />
                  </Form.Item>
                </Col>
              )}

              {hasSystemKeys && (
                <Col span={8}>
                  <Form.Item
                    name={[name, 'originalValue']}
                    label={t('classifiers.system_key')}
                    className="full-width"
                  >
                    <SystemKeyField
                      isEditing={editingIndex === name}
                      elementId={form.getFieldValue(['elements', name, 'id'])}
                      systemKeys={systemKeys}
                      onMoveFromChange={setSystemKeyMoveFrom}
                    />
                  </Form.Item>
                </Col>
              )}

              <Col span={extraColumns === 1 ? 12 : 20}>
                <EditableFormItem
                  isEditing={editingIndex === name}
                  name={[name, 'comments']}
                  label={t('common.comments')}
                  className="full-width"
                >
                  <Input placeholder={t('common.enter_text')} />
                </EditableFormItem>
              </Col>

              <Col span={4}>
                {name === editingIndex ? (
                  <Flex gap={8}>
                    <Button
                      icon={<CloseOutlined />}
                      onClick={() => onCancel(name)}
                    />

                    <SaveConfirm
                      title={`${t('common.save')}?`}
                      onConfirm={onSave}
                    >
                      <Button icon={<CheckOutlined />} type="primary" />
                    </SaveConfirm>
                  </Flex>
                ) : (
                  <Flex gap={8}>
                    <Button
                      icon={<EditOutlined />}
                      disabled={isDisabled}
                      onClick={() => setEditingIndex(name)}
                    />

                    <Popconfirm
                      title={t('classifiers.delete')}
                      description={t('common.delete_description')}
                      onConfirm={() => onDelete(name)}
                      placement="left"
                    >
                      <Button
                        icon={<DeleteOutlined />}
                        danger
                        disabled={isDisabled}
                      />
                    </Popconfirm>
                  </Flex>
                )}
              </Col>
            </Row>
          </div>
        )
      })}

      <Flex justify="end" align="center">
        <Form.Item>
          <SaveConfirm title={`${t('common.add_option')}?`} onConfirm={addItem}>
            <Button
              disabled={isDisabled}
              icon={<PlusOutlined />}
              className={styles.addButton}
              block
            >
              {t('common.add_option')}
            </Button>
          </SaveConfirm>
        </Form.Item>
      </Flex>

      <Pagination
        disabled={isDisabled}
        className="full-width"
        {...pagination}
        total={data?.totalCount}
      />
    </Flex>
  )
}
